/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { LogService, UtilsService } from '../../services';

@Component({
    selector: 'app-upload',
    template: `
        <div class="upload-wrapper">
            <button
                [id]="inputId + '-button'"
                [class.hide]="fileName && !buttonChangeText"
                class="{{buttonClass}} upload-button"
                (click)="openFileBrowser($event)">
                {{(fileName && fileName.length ? buttonChangeText : buttonText) | myLbTranslate}}
            </button>
            <input
                #uploadInput
                [attr.accept]="acceptedTypes"
                [id]="inputId"
                class="upload"
                type="file"
                (change)="onFileChange($event)"/>
            <span [hidden]="!fileName" class="file-name">{{fileName}}</span>
            <span [hidden]="!fileExtension" class="file-extension">{{fileExtension}}</span>
        </div>
    `,
    styleUrls: ['./upload.component.scss']
})

export class UploadComponent {
    /**
     * Get at the list item to scroll to them using native element.
     */
    @ViewChild('uploadInput', { static: true }) public input: any;

    /**
     * Pass in an id to associate a label.
     */
    @Input() public inputId: string = 'upload-file';

    /**
     * The default button text.
     */
    @Input() public buttonText: string = 'upload_choose_file';

    /**
     * The button text for change upload file.
     */
    @Input() public buttonChangeText: string = 'upload_change_file';

    /**
     * Button class.
     */
    @Input() public buttonClass: string = 'primary';

    /**
     * Comma separated string of accepted file types
     */
    @Input() public acceptedTypes: string;

    /**
     * Allow the input to be reset externally
     */
    @Input() set resetFileInput(state) {
        this._resetFileInput = state;
        if (this._resetFileInput) {
            this.resetSelectedFile();
            this._resetFileInput = false;
        }
    }

    /**
     * When the file is change tell the parent component.
     */
    @Output() public fileSelected: EventEmitter<any> = new EventEmitter();

    /**
     * Store the file name of the selected file.
     */
    public fileName: string;

    /**
     * Store the file extension of the selected file.
     */
    public fileExtension: string;

    /**
     * Store the reset state of the input.
     * @private
     */
    private _resetFileInput: boolean = false;

    constructor(
        private logService: LogService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Force 'click' the file input to show the file browser
     * @param event
     */
    public openFileBrowser(event: any): void {
        event.preventDefault();
        if (event.target.className.includes('upload-button')) {
            event.target.nextSibling.click();
        }
    }

    /**
     * When a file is selected tell the parent component.
     * @param event
     */
    public onFileChange(event: any): void {
        if (event.target.files && event.target.files.length) {
            const [file] = event.target.files;
            this.fileExtension = '.' + this.utilsService.getExtensionFromFilename(file.name);
            this.fileName = file.name.substr(0, file.name.lastIndexOf('.'));
            this.fileSelected.next(event);
        }
    }

    /**
     * Reset the file input value.
     * @private
     */
    private resetSelectedFile() {
        this.fileName = '';
        this.fileExtension = '';
        this.input.nativeElement.value = '';
        this.logService.info('UploadComponent:resetSelectedFile', 'Input type file reset');
    }
}
